export const formatSelectableLanguages = (aSelectableLanguages, aPrebooking, aPresence, aTargetLanguages, aSelectedTargetLangId, aLanguagePairs) => {
  let selectableLangsList = aSelectableLanguages.filter(lang => {
    return (
      (
        aPrebooking &&
        (
          (aPresence !== 'onsite' && lang.prebooking) ||
          (aPresence === 'onsite' && lang.onSite)
        )
      ) || (!aPrebooking && lang.onDemand)
    ) && !lang.nonLanguage && lang.languageTag !== 'prebooking' && !lang.prebookingId;
  });
  if (aTargetLanguages.length > 1) {
    selectableLangsList.forEach(lang => {
      lang.availability = aLanguagePairs[`${aSelectedTargetLangId}:${lang.id}`];
    });
  } else {
    selectableLangsList.forEach(lang => {
      lang.availability = aLanguagePairs[`${aTargetLanguages[0].id}:${lang.id}`];
    });
  }
  return selectableLangsList.filter(lang => lang.availability !== undefined);
};

export const getSelectableLangs = (user, aSelectableLangs, aSelectableLanguages) => {
  if (Date.now() - user.languagesTimestamp > 24 * 60 * 60 * 1000) {
    // TODO: Need to check
    // Sentry.addBreadcrumb({message: 'Wait for the most recent list'});
    if (aSelectableLangs !== []) {
      return [];
    }
  } else if (aSelectableLanguages.some(lang => lang.languageTag === 'prebooking')) {
    if (Date.now() - user.languagesTimestamp > 60 * 1000) {
      // TODO: Need to check
      // Sentry.addBreadcrumb({message: 'Wait for the most recent list (prebooking)'});
      if (aSelectableLangs !== []) {
        return [];
      }
    }
  }
  return null;
};

export const getLanguagePairs = (languagePairs) => {
  // TODO: different skills
  return languagePairs.reduce((memo, pair) => {
    const newValue = pair.availability || 99;
    const key = `${pair.targetLanguage.id}:${pair.interpretedLanguage.id}`;
    if (!memo[key] || memo[key] < newValue) {
      memo[key] = newValue;
    }
    return memo;
  }, {});
};

export const getTargetLanguages = (targetLanguages, selectedTargetLanguageId) => {
  if (selectedTargetLanguageId && targetLanguages.length > 1) {
    const foundTargetLanguage = targetLanguages?.find(l => l.id === selectedTargetLanguageId);
    if (foundTargetLanguage) {
      return foundTargetLanguage;
    }
    return targetLanguages[0];
  }
  return undefined;
};

// TUL-4996: the target (customer) language list returned by the backend is now narrowed by the
// customer's active contract, so a previously selected/persisted target language may no longer be
// offered. Return the selection only when it is still present in the list; otherwise fall back to
// the first available language (or null when the list is empty) so the UI never holds a target
// language that isn't selectable.
export const resolveSelectedTargetLanguageId = (targetLanguages, selectedTargetLanguageId) => {
  const list = Array.isArray(targetLanguages) ? targetLanguages : [];
  if (selectedTargetLanguageId && list.some(lang => lang.id === selectedTargetLanguageId)) {
    return selectedTargetLanguageId;
  }
  return list.length > 0 ? list[0].id : null;
};

export const getSelectableLanguage = (aSelectableLanguages, selectedLanguageId) => {
  const langIndex = aSelectableLanguages.findIndex(lang => lang.id === selectedLanguageId);
  if (langIndex > 0) {
    const selectedLanguage = [...aSelectableLanguages].splice(langIndex, 1)[0];
    return [selectedLanguage, ...aSelectableLanguages];
  }
  return aSelectableLanguages;
};

// Translation orders can go in either direction (e.g. Norwegian→Arabic or
// Arabic→Norwegian), so the ordering wizard's language dropdowns offer the
// customer's selectable languages plus their target languages. Target-language
// entries carry the actual language id in `languageId` (their `id` is the
// target-language row id), so they are normalized to the selectable-language
// shape before deduplicating by language id.
export const getTranslationOrderLanguages = (selectableLanguages, targetLanguages) => {
  const languages = [];
  const seen = new Set();

  (selectableLanguages || []).forEach(lang => {
    if (!lang?.id || seen.has(lang.id)) return;
    seen.add(lang.id);
    languages.push(lang);
  });

  (targetLanguages || []).forEach(lang => {
    const languageId = lang?.languageId || lang?.id;
    if (!languageId || seen.has(languageId)) return;
    seen.add(languageId);
    languages.push({ ...lang, id: languageId });
  });

  return languages.sort((l1, l2) => (l1.name || '').localeCompare(l2.name || ''));
};

export const LANGUAGE_LIST_MAX_AGE_MS = 60 * 1000;

// Whether the language list cached on the user can be reused instead of refetched: it must be
// recent, non-empty and localized for the current UI language. The backend names languages in the
// locale of the request, so a list fetched in Swedish is stale once the UI is Norwegian, however
// recent it is: after a language change in Settings the page reloads within the minute and used
// to keep the old names (TUL-6099). A list without a locale was cached by an older build.
export const isLanguageListFresh = (user, locale, now = Date.now()) =>
  Boolean(user?.languagesTimestamp)
  && now - user.languagesTimestamp < LANGUAGE_LIST_MAX_AGE_MS
  && user.languagesLocale === locale
  && Array.isArray(user.selectableLanguages)
  && user.selectableLanguages.length > 0;
